import React from 'react'
import VirtualList from 'react-tiny-virtual-list'

import clsx from 'clsx'

import Address from '../../base/Address'
import CallOut from '../../base/Callout'
import ShipmentForReturnInfoButton from '../../base/ShipmentForReturnInfoButton'
import ShipmentInfoButton from '../../base/ShipmentInfoButton'
import useTranslation from '../../context/hooks/useTranslation'
import { twm } from '../../lib/utils/twm'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  selectMorningScanState,
  setActiveShipmentDetails,
} from '../../redux/MorningScan'
import { MorningScanShipment } from '../../types/MorningScanResponse'
import { ReturnShipmentDataResponse } from '../../types/SetShipmentAsReturn'
import InfoCircleIcon from '../../vectors/InfoCircleIcon'

export interface ShipmentTableProps {
  width: number
  height: number
  data?: Array<MorningScanShipment>
  forReturns?: Array<ReturnShipmentDataResponse>
}

export const ShipmentTable: React.FC<ShipmentTableProps> = ({
  data = [],
  width,
  height,
  forReturns,
}) => {
  const { t } = useTranslation()

  const { activeShipmentDetails } = useAppSelector(selectMorningScanState)
  const dispatch = useAppDispatch()

  const handleViewDetails = (details: MorningScanShipment) => {
    dispatch(setActiveShipmentDetails(details))
  }

  const getReturnInfo = (shipment: MorningScanShipment) => {
    return forReturns?.find(
      (returnShipment) => shipment.shipmentId === returnShipment.shipmentId,
    )
  }

  return (
    <div className="flex w-fit flex-col">
      <div className="font-heading flex h-10 w-full flex-row items-center justify-between px-4 text-xs text-indigo-50">
        <span>{t('RETURNS_SCAN.ADDRESS')}</span>
        <span className="mr-2">{t('RETURNS_SCAN.ACTIONS')}</span>
      </div>
      {data.length ? (
        <VirtualList
          width={width}
          height={height}
          itemSize={56}
          itemCount={data.length}
          className="shadow-elevation-1 rounded-lg bg-white"
          renderItem={({ index, style }) => {
            const {
              address,
              city,
              country,
              postalCode,
              shipmentId,
              merchantName,
            } = data[index]
            const isActive = shipmentId === activeShipmentDetails?.shipmentId
            const returnInfo = getReturnInfo(data[index])

            const isLastRow = index === data.length - 1

            return (
              <div
                key={index}
                style={style}
                className={clsx(
                  'relative px-4 pt-[10px]',
                  isActive && 'bg-gray-34',
                )}
              >
                {isActive && (
                  <div className="absolute left-0 top-0 h-full w-[2px] bg-green-50" />
                )}
                <div
                  className={twm(
                    'flex flex-row justify-between border-b border-gray-42 pb-[10px]',
                    isLastRow && 'border-0',
                  )}
                >
                  <Address
                    address={address}
                    city={city}
                    country={country}
                    postalCode={postalCode}
                  />
                  {returnInfo ? (
                    <ShipmentForReturnInfoButton
                      onClick={() => handleViewDetails(data[index])}
                      merchantName={merchantName}
                      returnInfo={returnInfo}
                    />
                  ) : (
                    <ShipmentInfoButton
                      onClick={() => handleViewDetails(data[index])}
                    />
                  )}
                </div>
              </div>
            )
          }}
        />
      ) : (
        <div
          style={{ height, width }}
          className="shadow-elevation-1 flex w-auto flex-col items-center rounded-lg bg-white px-3 pt-5"
        >
          <CallOut icon={<InfoCircleIcon />}>
            {t('RETURNS_SCAN.KEEP_ON_HUB_PLACEHOLDER')}
          </CallOut>
        </div>
      )}
    </div>
  )
}

export default ShipmentTable
